import { Alert, Button, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { ActionCard } from "../../../../components/ActionCard";
import { useDisclosure } from "@mantine/hooks";
import { useTRPC } from "../../../../trpc";
import { pascalCase } from "es-toolkit";
import { FullPageLoader } from "../../../../components/FullPageLoader";
import { AddressDisplay } from "./AddressDisplay";
import { AddressModal } from "./AddressModal";
import { PaymentMethodForm } from "../../../../components/PaymentMethodForm";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { IconAlertCircle } from "@tabler/icons-react";
import { isFeatureEnabled } from "../../../../utils/posthog";

export const PaymentsSettings = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const { data: account } = useQuery(trpc.getCurrentAccount.queryOptions());
  const { data: paymentMethod } = useQuery(trpc.getCurrentPaymentMethod.queryOptions());

  const [
    addressOpened,
    {
      open: openAddress,
      close: closeAddress,
    },
  ] = useDisclosure(false);

  const [
    methodOpened,
    {
      open: openMethod,
      close: closeMethod,
    },
  ] = useDisclosure(false);

  const isPaymentsEnabled = isFeatureEnabled("enable-payments");

  if (!isPaymentsEnabled) {
    return (
      <Alert
        variant="light"
        icon={<IconAlertCircle size={24} />}
        title="Subscriptions are not enabled"
        color="yellow"
      >
        <Text size="sm">
          <FormattedMessage
            id="text.subscriptionsNotEnabled"
            defaultMessage="Subscriptions are temporarily not enabled. Please try again later."
          />
        </Text>
      </Alert>
    );
  }

  if (!account) {
    return <FullPageLoader />;
  }

  const card = paymentMethod?.card;

  return (
    <Stack gap="lg">
      <ActionCard
        title={intl.formatMessage({ id: "title.paymentMethod", defaultMessage: "Payment method" })}
        subTitle={card
          ? `${pascalCase(card.brand)} •••• ${card.last4}`
          : intl.formatMessage({ id: "text.noPaymentMethod", defaultMessage: "You don't have a payment method yet." })}
      >
        <Button onClick={openMethod} variant="primary">
          {card
            ? <FormattedMessage id="button.change" defaultMessage="Change" />
            : <FormattedMessage id="button.add" defaultMessage="Add" />}
        </Button>
      </ActionCard>

      {card && (
        <ActionCard
          title={intl.formatMessage({ id: "title.billingAddress", defaultMessage: "Billing address" })}
          subTitle={<AddressDisplay billingAddress={account.billingAddress} />}
        >
          <Button onClick={openAddress} variant="primary">
            <FormattedMessage id="button.edit" defaultMessage="Edit" />
          </Button>
        </ActionCard>
      )}

      {addressOpened && (
        <AddressModal
          account={account}
          opened={addressOpened}
          onClose={closeAddress}
        />
      )}

      <Modal
        size="lg"
        opened={methodOpened}
        onClose={closeMethod}
        scrollAreaComponent={ScrollArea.Autosize}
        title={card
          ? <FormattedMessage id="title.changePaymentMethod" defaultMessage="Change payment method" />
          : <FormattedMessage id="title.addPaymentMethod" defaultMessage="Add payment method" />}
      >
        <PaymentMethodForm
          account={account}
          onChangePayment={closeMethod}
          onCancel={closeMethod}
        />
      </Modal>
    </Stack>
  );
};
